<template>
  <section ref="baseSelect" class="base-select-dropdown">
    <BaseButton
      :id="id"
      type="button"
      class="base-select-dropdown__button"
      :class="{
        'psuedo-icon': psuedoIcon,
        'base-select-dropdown__button--active': isActive,
        'base-select-dropdown__button--disabled': disabled,
        'base-select-dropdown__button--invalid': invalid,
      }"
      :color="buttonColor"
      :text-color="buttonTextColor"
      :size="buttonSize"
      :button-style="buttonStyle"
      :disabled="disabled"
      @click.self="toggleIsActive()"
    >
      <span
        ref="textContainer"
        class="base-select-dropdown__button-text"
        :class="{
          'base-select-dropdown__button-text--active': isActive,
        }"
        @click.self="toggleIsActive()"
      >
        <slot>
          <span
            v-if="!tags || selectedOptions.length <= 0"
            @click.self="toggleIsActive()"
          >
            {{ formattedDisplayText }}
          </span>
          <div v-else class="base-select-dropdown__tag-container">
            <div
              v-for="(tag, index) in selectedTags"
              :key="`base-select-dropdown-tag-${index}`"
              class="base-select-dropdown__tag"
            >
              <span class="base-select-dropdown__tag-text">
                {{ tag }}
              </span>
              <button
                v-if="index <= 1"
                type="button"
                class="base-select-dropdown__tag-button"
                @click="removeTag(tag)"
              >
                <IconCircleClose width="12" height="12" />
              </button>
            </div>
          </div>
        </slot>
      </span>
      <div
        v-if="altIcon"
        class="base-select-dropdown__alt-icon"
        @click.self="toggleIsActive()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="8"
          height="8"
          viewBox="0 0 8 8"
          fill="none"
        >
          <path
            d="M7.94531 0.554688L4.95117 7.04883L3.9668 7.04883L0.583008 0.554687L2.2168 0.554688L4.41797 4.9502L6.33203 0.554688H7.94531Z"
            fill="#585B60"
          />
        </svg>
      </div>
      <IconChevronDown
        v-if="!altIcon && !psuedoIcon"
        :is-active="isActive"
        :color="isActive ? 'green-mid' : iconFill"
        is-solid
        :width="8"
        :height="10"
        @click.self="toggleIsActive()"
      />
    </BaseButton>
    <ul
      class="base-select-dropdown__list"
      :class="{
        'base-select-dropdown__list--active': isActive,
      }"
      :style="listCoordinates"
    >
      <li
        v-if="!options || options.length <= 0"
        class="base-select-dropdown__list-item base-select-dropdown__list-item--no-options"
      >
        <span
          class="base-select-dropdown__list-item-text base-select-dropdown__list-item-text--no-options"
        >
          No options available
        </span>
      </li>
      <li
        v-if="multiselect && hasSelectAll && options.length > 0"
        class="base-select-dropdown__list-item"
      >
        <BaseCheckbox
          v-model="hasSeletedAll"
          name="user-select-all"
          reverse
          label-flex
        >
          Select all
        </BaseCheckbox>
      </li>
      <li
        v-for="(option, index) in options"
        :key="`base-select-dropdown-item-${index}`"
        class="base-select-dropdown__list-item"
        :class="{
          'base-select-dropdown__list-item--selected': isOptionSelected(option),
        }"
      >
        <template v-if="multiselect">
          <BaseCheckbox
            :name="`base-select-dropdown-item-${index}`"
            :value="option.value"
            :reverse="!leftAligned"
            label-flex
            v-model="selectedOptions"
          >
            {{ option.label }}
          </BaseCheckbox>
        </template>
        <template v-else>
          <span
            :name="`base-select-dropdown-item-${index}`"
            class="base-select-dropdown__list-item-text"
            @click="selectOption(option)"
          >
            {{ option.label }}
          </span>
        </template>
      </li>
    </ul>
  </section>
</template>

<script>
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';
  import IconChevronDown from '~/components/icon/IconChevronDown.vue';
  import IconCircleClose from '~/components/icon/IconCircleClose.vue';

  export default {
    name: 'BaseSelectDropdown',
    emits: ['update:modelValue', 'clicked-outside'],
    components: {
      BaseButton,
      BaseCheckbox,
      IconChevronDown,
      IconCircleClose,
    },
    props: {
      id: {
        type: String,
        required: true,
      },
      multiselect: {
        type: Boolean,
        required: false,
        default: false,
      },
      modelValue: {
        type: [Array, String, Number],
        required: false,
        default: null,
      },
      options: {
        type: Array,
        required: false,
        default: () => [],
      },
      placeholderText: {
        type: String,
        required: false,
        default: 'Select an option',
      },
      buttonStyle: {
        type: String,
        required: false,
        default: 'flat',
        validator: (value) => {
          const STYLES = ['normal', 'flat'];
          return STYLES.includes(value);
        },
      },
      buttonColor: {
        type: String,
        required: false,
        default: 'white',
        validator: (value) => {
          const COLORS = [
            'primary',
            'secondary',
            'cta',
            'success',
            'warning',
            'danger',
            'grey',
            'dark-grey',
            'charcoal',
            'green',
            'green-mid',
            'white',
            'black',
          ];
          return COLORS.includes(value);
        },
      },
      buttonTextColor: {
        type: String,
        required: false,
        default: 'charcoal',
        validator: (value) => {
          const COLORS = [
            'primary',
            'secondary',
            'cta',
            'success',
            'warning',
            'danger',
            'grey',
            'dark-grey',
            'charcoal',
            'green',
            'green-mid',
            'white',
            'black',
          ];
          return COLORS.includes(value);
        },
      },
      menuColor: {
        type: String,
        required: false,
        default: 'grey',
        validator: (value) => {
          const COLORS = ['grey', 'white', 'black'];
          return COLORS.includes(value);
        },
      },
      buttonSize: {
        type: String,
        required: false,
        default: 'flat',
        validator: (value) => {
          const SIZES = ['xs', 'sm', 'md', 'lg', 'xl', 'flat'];
          return SIZES.includes(value);
        },
      },
      iconFill: {
        type: String,
        required: false,
        default: 'charcoal',
        validator: (value) => {
          const COLORS = [
            'primary',
            'secondary',
            'cta',
            'success',
            'warning',
            'danger',
            'grey',
            'dark-grey',
            'charcoal',
            'green',
            'green-mid',
            'white',
            'black',
          ];
          return COLORS.includes(value);
        },
      },
      altIcon: {
        type: Boolean,
        default: false,
      },
      psuedoIcon: {
        type: Boolean,
        default: false,
      },
      invalid: Boolean,
      disabled: Boolean,
      hasSelectAll: Boolean,
      tags: Boolean,
      leftAligned: Boolean,
    },
    data: () => ({
      internalSelectedValue: null,
      hasSeletedAll: false,
      isActive: false,
      formattedDisplayText: '',
      listCoordinates: {
        top: 0,
        bottom: 'auto',
        left: 0,
        width: 0,
        maxHeight: 0,
      },
    }),
    computed: {
      selectedOptions: {
        get() {
          return this.internalSelectedValue;
        },
        set(newValue) {
          let newSelectedValue = newValue;
          if (this.multiselect && newSelectedValue === null)
            newSelectedValue = [];
          else if (!this.multiselect && newSelectedValue === null)
            newSelectedValue = '';
          this.internalSelectedValue = newSelectedValue;
          this.$emit('update:modelValue', newSelectedValue);
        },
      },
      isValueTypeArray() {
        if (
          typeof this.internalSelectedValue === 'object' &&
          this.internalSelectedValue !== null
        )
          return Array.isArray(this.internalSelectedValue);
        return typeof this.internalSelectedValue === 'array';
      },
      selectedTags() {
        if (
          !this.tags ||
          !this.selectedOptions ||
          this.selectedOptions.length === 0
        )
          return [];

        const selectedTagsArray = [];
        let aditionalTotal = 0;

        this.selectedOptions.forEach((selectedOption, index) => {
          if (index <= 1) {
            selectedTagsArray.push(selectedOption);
            return;
          }
          aditionalTotal += 1;
        });

        if (aditionalTotal > 0) {
          selectedTagsArray.push(`+${aditionalTotal}`);
        }

        return selectedTagsArray;
      },
    },
    watch: {
      modelValue(newValue, oldValue) {
        // IF THE VALUE PROP IS NOT USED I NEEDED TO ADD INTERNAL VALUE SO IT WOULD DISPLAY THE SELECTED OPTION
        // BECAUSE OF THIS IF THEY ARE USING THE VALUE PROP AND THEY EXTERNALLY CHANGE THE VALUE I NEED TO UPDATE THE INTERNAL VALUE
        if (newValue !== oldValue) {
          this.internalSelectedValue = newValue;
        }
      },
      options(newValue) {
        if (newValue.length <= 0) {
          this.hasSeletedAll = false;
        }
        this.setFormattedDisplayText();
      },
      selectedOptions(newValue) {
        if (this.multiselect && newValue.length >= this.options.length) {
          this.hasSeletedAll = true;
        } else {
          this.hasSeletedAll = false;
        }
        this.setFormattedDisplayText();
      },
      isActive(newValue) {
        if (newValue) {
          this.setListCoordinates();
        }
      },
      hasSeletedAll(value) {
        if (value) {
          this.selectedOptions = this.options.map((option) => option.value);
          return;
        }
        this.selectedOptions = [];
      },
    },
    created() {
      this.internalSelectedValue = this.modelValue;
    },
    mounted() {
      window.addEventListener('resize', this.closeList);
      window.addEventListener('scroll', this.closeList);

      window.addEventListener('click', this.handleWindowClick);
      this.setFormattedDisplayText();
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.closeList);
      window.removeEventListener('scroll', this.closeList);
      window.removeEventListener('click', this.handleWindowClick);
    },
    methods: {
      removeTag(tag) {
        if (!this.tags) return;
        this.selectedOptions = this.selectedOptions.filter(
          (selectedOption) => selectedOption !== tag
        );
      },
      isOptionSelected(option) {
        if (!this.isValueTypeArray)
          return this.selectedOptions === option.value;
        return this.selectedOptions.some(
          (selectedOption) => selectedOption.value === option.value
        );
      },
      toggleIsActive() {
        if (this.disabled) return;
        this.isActive = !this.isActive;
      },
      closeList() {
        this.isActive = false;
      },
      handleWindowClick(event) {
        const component = this.$refs.baseSelect;
        if (component && !component.contains(event.target)) {
          this.isActive = false;
          this.$emit('clicked-outside');
        }
      },
      setFormattedDisplayText() {
        // IF THE USER HAS PASSED IN A SLOT, DON'T DO ANYTHING BECAUSE THIS WOND BE USED
        // if (Object.keys(this.$slots).includes('default')) return // TODO: FIX THIS, VUE 3 BROKE IT

        if (
          this.tags ||
          !this.internalSelectedValue ||
          this.options.length <= 0 ||
          (this.multiselect && this.internalSelectedValue.length <= 0) ||
          !this.$refs.textContainer
        ) {
          this.formattedDisplayText = this.placeholderText;
          return;
        }

        if (!this.multiselect) {
          const displayText = this.options.find(
            (item) => item.value === this.internalSelectedValue
          );
          this.formattedDisplayText = displayText
            ? displayText.label
            : this.placeholderText;
          return;
        }

        const selectedOptionsArray = this.selectedOptions
          ? [...this.selectedOptions]
          : [];

        const selectedOptionNames = this.options
          .filter((option) => {
            return selectedOptionsArray.some(
              (selectedOption) => selectedOption === option.value
            );
          })
          .map((option) => option.label);
        const selectedOptionNamesString = selectedOptionNames.join(', ');

        //THE WIDTH OF THE TEXT CONTAINER
        const textContainerWidth = this.$refs.textContainer.clientWidth;
        const availableTextWidth = textContainerWidth / 8;

        // IF THE TEXT IS WIDER THAN THE CONTAINER, TRUNCATE IT
        if (selectedOptionNamesString.length > availableTextWidth) {
          this.formattedDisplayText = `${selectedOptionNamesString.substring(0, availableTextWidth)}...`;
          return;
        }

        this.formattedDisplayText = selectedOptionNamesString;
      },
      selectOption(option) {
        if (this.multiselect) return;
        this.selectedOptions = option.value;
        this.isActive = false;
      },
      setListCoordinates() {
        if (!this.$refs.baseSelect) return;

        const baseSelectCoordinates =
          this.$refs.baseSelect.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const spaceBelowSelect =
          windowHeight -
          (baseSelectCoordinates.top + baseSelectCoordinates.height);
        const spaceAboveSelect = baseSelectCoordinates.top;
        const isSpaceBelowSelect =
          spaceBelowSelect > 300 || spaceBelowSelect > spaceAboveSelect;

        this.listCoordinates = {
          top: isSpaceBelowSelect
            ? `${baseSelectCoordinates.top + baseSelectCoordinates.height}px`
            : 'auto',
          bottom: isSpaceBelowSelect
            ? 'auto'
            : `${windowHeight - baseSelectCoordinates.top}px`,
          left: `${baseSelectCoordinates.left}px`,
          width: `${baseSelectCoordinates.width}px`,
          maxHeight: isSpaceBelowSelect
            ? `${spaceBelowSelect}px`
            : `${spaceAboveSelect}px`,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .base-select-dropdown {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    border: none;
    border-radius: 4px;
    padding: 0;
    margin: 0;

    &__tag {
      position: relative;
      box-sizing: border-box;
      pointer-events: none;
      display: flex;
      width: fit-content;
      background-color: var(--itr-grey-soft);

      margin: 0;
      background-color: #f0f2f5;
      border-radius: 4px;
      color: #999;
      font-size: 14px;
      height: 24px;
      line-height: 24px;
      padding: 0 8px;
      white-space: nowrap;
      max-width: calc(100% - 34px);
      vertical-align: top;
      cursor: default;

      &-text {
        font-size: 10px;
        font-weight: 700;
        padding: 2px 4px;
      }
      &-button {
        pointer-events: all;
        background-color: inherit;
        border: none;
        cursor: pointer;
      }
    }

    &__tag-container {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    &__button {
      display: flex;
      align-items: center;
      padding: 12px 20px;
      border: 1px solid var(--itr-grey-mid);
      line-height: 24px;

      &--active {
        border-color: var(--itr-green-mid);
      }
      &--disabled {
        border-color: var(--itr-grey-mid);
        background-color: var(--itr-grey-soft);
        color: var(--itr-grey-mid);
      }
      &--invalid {
        border-color: var(--itr-color-danger);
      }

      &-text {
        flex: 1 auto;
        text-align: left;
        font-size: 16px;
        font-weight: 400;
        font-family: 'Open Sans', sans-serif;
        color: var(--itr-grey-mid);

        width: 100%;
        overflow: hidden;

        &--active {
          color: var(--itr-charcoal);
        }
      }
    }
    &__alt-icon {
      margin-left: 8px;
    }

    &__list {
      position: fixed;
      display: none;
      visibility: hidden;
      opacity: 0;
      flex-direction: column;
      margin: 0;
      padding: 0;
      list-style: none;
      z-index: 1;
      overflow-y: auto;
      overflow-x: hidden;
      background-color: var(--itr-white);

      border-radius: 10px;
      border: 1px solid var(--itr-grey-mid);

      &--active {
        display: flex;
        visibility: visible;
        opacity: 1;
      }

      &-item {
        display: flex;
        align-items: center;
        width: 100%;
        padding: 4px 10px;
        cursor: pointer;
        user-select: none;
        transition: background-color 200ms ease-in-out;

        &--no-options {
          cursor: default;
          padding: 6px 10px;

          &:hover {
            background-color: transparent !important;
          }
        }

        label {
          font-size: 16px;
          font-weight: 400;
          color: var(--itr-charcoal);
        }

        &-text {
          flex: 1 auto;
          text-align: left;
          font-size: 16px;
          font-family: 'Open Sans', sans-serif;

          width: 100%;
          overflow: hidden;

          &--no-options {
            color: var(--itr-grey-mid);
            font-style: italic;
          }
        }

        &--selected {
          background-color: var(--itr-grey-soft);
        }

        &:hover {
          background-color: var(--itr-grey);
        }
      }
    }
  }
</style>
